// ============================================================================
// Analysis types — normalized shapes consumed by the pure aggregation
// functions in utils/analytics. See Documents/202607-ANALYSIS_DASHBOARD_DESIGN.md
// ============================================================================

export type Granularity = 'day' | 'week' | 'month' | 'year';

export interface AnalysisStaySite {
  siteId: number;
  siteAlias: string;
  siteCost: number;
  status: string;
  cancelledAt?: string;
  cancellationReason?: string;
  addedAfterBooking?: boolean;
}

/**
 * One reservation version, normalized for analytics.
 * `checkOut` is derived (`checkIn + days`); occupancy nights are
 * [checkIn, checkOut) per active site.
 */
export interface AnalysisStay {
  id: number;
  confirmationCode: string;
  checkIn: string; // YYYY-MM-DD
  checkOut: string; // YYYY-MM-DD (exclusive)
  days: number;
  cost: number;
  status: string;
  createdAt?: string; // ISO timestamp
  memberId?: number;
  customerId?: number;
  adults: number;
  children: number;
  pets: number;
  vehicles: number;
  isCurrentVersion: boolean;
  supersededByReservationId?: number;
  supersedesReservationId?: number;
  hasPaymentLink: boolean;
  sites: AnalysisStaySite[];
}

export interface SeriesPoint {
  bucket: string; // YYYY-MM-DD (day/week bucket start), YYYY-MM, or YYYY
  label: string; // display label for the x-axis
  value: number;
}

export interface RevenuePoint extends SeriesPoint {
  bookings: number;
}

export type AnomalySeverity = 'critical' | 'serious' | 'warning' | 'info';

export interface AnomalyFinding {
  severity: AnomalySeverity;
  type: string;
  reservationId: number;
  confirmationCode: string;
  message: string;
}

/** Statuses that contribute to revenue and occupancy. */
export const REVENUE_STATUSES = new Set([
  'confirmed',
  'checked_in',
  'checked_out',
]);

export function isRevenueStay(stay: AnalysisStay): boolean {
  return stay.isCurrentVersion && REVENUE_STATUSES.has(stay.status);
}
